import { Injectable, NgZone } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { Device } from '@twilio/voice-sdk';
import { TmQueueService } from '@app/_core/services/tm-queue.service';

export interface PreflightUiResult {
  status: 'idle' | 'running' | 'connected' | 'completed' | 'failed' | 'cancelled' | 'passed';
  message: string;
  report?: any;
  error?: any;
  latestSample?: any;
  warnings?: any[];
}

@Injectable({
  providedIn: 'root'
})
export class TwilioPreflightService {
  private preflightTest: any;

  constructor(
    private http: HttpClient,
    private ngZone: NgZone,
    private tmQueueService: TmQueueService,
  ) {
    console.log('Twilio Test');
  }


  getVoiceToken(): Observable<{ token: string }> {
    return this.tmQueueService.GetTwilioToken().pipe(
      map((response: any) => {
        return {
          token: response.Response.Data.Token
        };
      })
    );
  }
  runPreflight(
    token: string,
    selectedInputDeviceId?: string,
    selectedOutputDeviceId?: string
  ): Observable<PreflightUiResult> {
    return new Observable<PreflightUiResult>((observer) => {
      observer.next({
        status: 'running',
        message: 'Running diagnostics...'
      });

      const options: any = {
        codecPreferences: ['opus', 'pcmu'],
        //edge: ['ashburn', 'roaming'],
        closeProtection: true,
        logLevel: 1,
        maxCallSignalingTimeoutMs: 30000,
        tokenRefreshMs: 30000,
        networkQuality: true,
        enableIceRestart: true,
        forceAggressiveIceNomination: true,
        disableAudioContextSounds: true,
        enableImprovedSignalingErrorPrecision: true,
        fakeMicInput: false,
        signalingTimeoutMs: 10000
      };

      /**
       * Twilio Preflight options can include:
       * edge, codecPreferences, fakeMicInput, iceServers, logLevel, signalingTimeoutMs.
       */
      this.preflightTest = Device.runPreflight(token, options);

      this.preflightTest.on('connected', () => {
        this.ngZone.run(() => {
          observer.next({
            status: 'connected',
            message: 'Connected to Twilio. Testing audio/network quality...'
          });
        });
      });

      this.preflightTest.on('sample', (sample: any) => {
        this.ngZone.run(() => {
          observer.next({
            status: 'running',
            message: 'Collecting WebRTC samples...',
            latestSample: sample
          });
        });
      });

      this.preflightTest.on('warning', (warningName: string, warningData: any) => {
        this.ngZone.run(() => {
          observer.next({
            status: 'running',
            message: `Warning detected: ${warningName}`,
            warnings: [
              {
                name: warningName,
                data: warningData
              }
            ]
          });
        });
      });

      this.preflightTest.on('completed', (report: any) => {
        this.ngZone.run(() => {
          observer.next({
            status: 'completed',
            message: 'Diagnostics completed successfully.',
            report
          });

          observer.complete();
        });
      });

      this.preflightTest.on('failed', (error: any) => {
        this.ngZone.run(() => {
          observer.next({
            status: 'failed',
            message: 'Diagnostics failed.',
            error
          });

          observer.error(error);
        });
      });

      return () => {
        this.stopPreflight();
      };
    });
  }

  stopPreflight(): void {
    if (this.preflightTest) {
      this.preflightTest.stop();
      this.preflightTest = null;
    }
  }
}